import React, { useContext, useEffect, useState } from "react";
import { ImageFile, ImagesResponse } from "../../Models/Models";
import { SiteContext } from "../../Contexts";
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { ImageService } from "../../Services/ImageService";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

const ImageSelectorComponent = (props: {
    handleImageClick: (image: ImageFile) => void;
    refreshTrigger: number;
    onStateChange?: (loading: boolean, empty: boolean) => void;
    /** Image ids already used by the caller; rendered with a check badge. */
    selectedIds?: number[];
    accentColor?: string;
}) => {
    const imagesPerPage = React.useMemo(() => (window.innerHeight <= 1080 ? 4 : 8), []);
    const [imageData, setImageData] = useState<ImagesResponse | null>(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const { currentSite }: any = useContext(SiteContext);
    const { filterImages } = ImageService();

    const accent = props.accentColor ?? 'var(--st-accent)';

    const handleLoad = async (pageNum: number) => {
        setLoading(true);
        if (currentSite.id !== -1) {
            const data = await filterImages({
                websiteId: currentSite.id,
                limit: imagesPerPage,
                offset: (pageNum - 1) * imagesPerPage
            });
            setImageData(data);
            setLoading(false);
            props.onStateChange?.(false, data.uploadedImages.length === 0);
        }
    };

    useEffect(() => {
        void handleLoad(1);
    }, [currentSite]);

    useEffect(() => {
        if (imageData && imageData.totalCount != null) {
            const maxOffset = Math.max(0, imageData.totalCount - 1);
            const currentOffset = (page - 1) * imagesPerPage;

            if (currentOffset > maxOffset) {
                const newPage = Math.max(1, Math.ceil(imageData.totalCount / imagesPerPage));
                setPage(newPage);
                void handleLoad(newPage);
            } else {
                void handleLoad(page);
            }
        }
    }, [props.refreshTrigger]);

    const handlePageChange = (newPageNum: number) => {
        setPage(newPageNum);
        void handleLoad(newPageNum);
    };

    return (
        <>
            <div
                className="row mt-4 st-image-selector-grid"
                style={{ '--img-accent': accent } as React.CSSProperties}
            >
                {loading ? (
                    <div className="col-12 text-center">
                        <p>Načítavam obrázky...</p>
                    </div>
                ) : (!imageData || imageData.uploadedImages.length === 0) && !loading ? (
                    <div className="col-12 text-center">
                        <p>Žiadne obrázky</p>
                    </div>
                ) : (
                    imageData.uploadedImages.map(image => {
                        const isSelected = props.selectedIds?.includes(image.id) ?? false;
                        return (
                            <div key={image.id} className="col-3 mb-4 align-content-center text-center">
                                <div className="st-image-thumb-wrap">
                                    <img
                                        src={image.filePath.replace("original.jpg", "180x120.webp")}
                                        alt={image.alt}
                                        className={`img-thumbnail st-image-thumb${isSelected ? ' st-image-thumb--selected' : ''}`}
                                        onClick={() => props.handleImageClick(image)}
                                    />
                                    {isSelected && (
                                        <span className="st-image-check-badge">
                                            <FontAwesomeIcon icon={faCheck} />
                                        </span>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {!loading && imageData && imageData.uploadedImages.length > 0 && (
                <div className="mt-2">
                    <PaginationControl
                        page={page}
                        between={4}
                        total={imageData.totalCount}
                        limit={imagesPerPage}
                        changePage={(page) => handlePageChange(page)}
                        ellipsis={1}
                    />
                </div>
            )}
        </>
    );
};

export default ImageSelectorComponent;
